import React, { useState, useEffect } from 'react';
import TokenModal from './TokenModal';
import VoucherModal from './VoucherModal';
import { resolveVioAssetUrl } from '../utils/vioAssetUrl';
import './RewardGrid.css';

const DAYS = [1, 2, 3, 4, 5, 6, 7];

/** Right column: ISO 4217 → symbol via Intl; percentage → `n%`; else `n` + currency string. */
function formatVoucherValueWithSymbol(reward) {
  if (reward.type !== 'voucher') return null;
  const v = Number(reward.voucherValue);
  if (!Number.isFinite(v) || v <= 0) return null;

  const vt = String(reward.voucherValueType || '').toLowerCase();
  if (vt === 'percentage' || vt.includes('percent')) {
    return `${v}%`;
  }

  const code = (reward.voucherValueCurrency || '').trim().toUpperCase();
  if (code && /^[A-Z]{3}$/.test(code)) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(v);
    } catch {
      /* fall through */
    }
  }
  const raw = (reward.voucherValueCurrency || '').trim();
  return raw ? `${v} ${raw}` : String(v);
}

/** @returns {{ label: string, variant: 'private' | 'public' } | null} */
function voucherTypeChipModel(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (!s) return null;
  const lc = s.toLowerCase();
  if (lc.includes('private')) return { label: 'Private', variant: 'private' };
  if (lc.includes('public')) return { label: 'Public', variant: 'public' };
  return { label: s, variant: 'public' };
}

/** Resolves VIO path; shows placeholder on missing URL or load error. */
function RewardPreviewMedia({ rawPath, className, placeholder, resolveBase }) {
  const resolved = resolveVioAssetUrl(rawPath, { baseUrl: resolveBase });
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [rawPath, resolved, resolveBase]);

  if (!resolved || failed) {
    return (
      <div className={`${className} reward-preview-thumb--placeholder`} aria-hidden>
        {placeholder === 'voucher' ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 7V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2" />
            <path d="M4 7h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" />
            <path d="M9 12h6" />
          </svg>
        ) : (
          <span className="reward-preview-thumb-emoji">🪙</span>
        )}
      </div>
    );
  }

  return (
    <img
      src={resolved}
      alt=""
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

function formatDraftTime(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return '';
  }
}

function RewardGrid({
  rewards,
  onChange,
  onSave,
  saving,
  saveError,
  onDismissSaveError,
  localDraft,
  onSaveDraft,
  onRestoreDraft,
  onDiscardDraft,
  vioAssetBaseUrl = '',
  hasUnsavedChanges = true,
}) {
  const [modalDay, setModalDay] = useState(null);
  const [modalType, setModalType] = useState(null); // 'token' | 'voucher'

  const openModal = (day, type) => {
    setModalDay(day);
    setModalType(type);
  };

  const closeModal = () => {
    setModalDay(null);
    setModalType(null);
  };

  const handleApply = (dayIndex, rewardData) => {
    const updated = [...rewards];
    updated[dayIndex] = { ...updated[dayIndex], ...rewardData };
    onChange(updated);
  };

  const clearDay = (dayIndex) => {
    const updated = [...rewards];
    updated[dayIndex] = {
      day: dayIndex + 1,
      type: '',
      tokenId: '',
      tokenName: '',
      tokenSymbol: '',
      tokenAmount: 0,
      tokenIcon: '',
      voucherId: '',
      campaignVoucherSchemaId: '',
      campaignId: '',
      voucherName: '',
      voucherImage: '',
      voucherValue: 0,
      voucherValueCurrency: '',
      voucherValueType: '',
      voucherType: '',
      voucherCategory: '',
    };
    onChange(updated);
  };

  const allConfigured = rewards.every(
    (r) => r.type === 'token' || r.type === 'voucher'
  );

  const getReward = (dayIndex) => rewards[dayIndex] || {};

  return (
    <div className="reward-grid">
      <p className="reward-grid-intro">
        Assign a <strong>token</strong> or <strong>voucher</strong> for each streak day. Use the
        buttons to open the picker — all seven days must be filled before you can save to the
        server.
      </p>

      {localDraft?.savedAt && (
        <div className="reward-draft-banner" role="status">
          <span>
            Local draft saved at {formatDraftTime(localDraft.savedAt)} (not on server).
          </span>
          <span className="reward-draft-banner-actions">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={onRestoreDraft}
            >
              Restore draft
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={onDiscardDraft}
            >
              Discard draft
            </button>
          </span>
        </div>
      )}

      {saveError && (
        <div className="reward-save-error" role="alert">
          <div className="reward-save-error-body">
            <strong>Save failed</strong>
            <p className="reward-save-error-msg">{saveError}</p>
          </div>
          {onDismissSaveError && (
            <button
              type="button"
              className="reward-save-error-dismiss"
              onClick={onDismissSaveError}
              aria-label="Dismiss"
            >
              ×
            </button>
          )}
        </div>
      )}

      <div className="reward-grid-cards">
        {DAYS.map((day) => {
          const idx = day - 1;
          const r = getReward(idx);
          const hasReward = r.type === 'token' || r.type === 'voucher';
          const voucherValueFormatted = r.type === 'voucher' ? formatVoucherValueWithSymbol(r) : null;
          const voucherTypeChip = r.type === 'voucher' ? voucherTypeChipModel(r.voucherType) : null;

          return (
            <div
              key={day}
              className={`reward-day-card${day === 7 ? ' reward-day-card--final' : ''}`}
            >
              <div className="reward-day-card__aside">
                <span className="reward-day-num" aria-hidden>
                  {day}
                </span>
                <span className="reward-day-label">Day</span>
                {day === 7 && (
                  <span className="reward-day-final-badge">Final</span>
                )}
              </div>

              <div className="reward-day-card__body">
                <div
                  className="reward-type-toggle"
                  role="group"
                  aria-label={`Day ${day} reward type`}
                >
                  <button
                    type="button"
                    className={`reward-type-btn${
                      r.type === 'token' ? ' reward-type-btn--active-token' : ''
                    }`}
                    onClick={() => openModal(idx, 'token')}
                  >
                    Token
                  </button>
                  <button
                    type="button"
                    className={`reward-type-btn${
                      r.type === 'voucher' ? ' reward-type-btn--active-voucher' : ''
                    }`}
                    onClick={() => openModal(idx, 'voucher')}
                  >
                    Voucher
                  </button>
                </div>

                <div className="reward-day-preview">
                  {hasReward ? (
                    <div className="reward-preview-filled">
                      {r.type === 'token' && (
                        <RewardPreviewMedia
                          rawPath={r.tokenIcon}
                          className="reward-preview-thumb"
                          placeholder="token"
                          resolveBase={vioAssetBaseUrl}
                        />
                      )}
                      {r.type === 'voucher' && (
                        <RewardPreviewMedia
                          rawPath={r.voucherImage}
                          className="reward-preview-thumb"
                          placeholder="voucher"
                          resolveBase={vioAssetBaseUrl}
                        />
                      )}
                      {r.type === 'token' && (
                        <div className="reward-preview-text">
                          <span className="reward-preview-name">
                            {r.tokenName || r.tokenSymbol || 'Token'}
                          </span>
                          <span className="reward-preview-meta">
                            {r.tokenAmount} {r.tokenSymbol}
                          </span>
                        </div>
                      )}
                      {r.type === 'voucher' && (
                        <>
                          <div className="reward-preview-voucher-main">
                            <span className="reward-preview-name reward-preview-name--voucher">
                              {r.voucherName || 'Voucher'}
                            </span>
                            <div className="reward-preview-voucher-row">
                              {r.voucherCategory ? (
                                <span className="reward-preview-category">{r.voucherCategory}</span>
                              ) : null}
                              {voucherTypeChip ? (
                                <span
                                  className={`voucher-chip voucher-chip--${voucherTypeChip.variant}`}
                                >
                                  {voucherTypeChip.label}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          {voucherValueFormatted ? (
                            <div className="reward-preview-voucher-value" aria-label="Voucher value">
                              {voucherValueFormatted}
                            </div>
                          ) : null}
                        </>
                      )}
                      <button
                        type="button"
                        className="reward-preview-clear"
                        onClick={() => clearDay(idx)}
                        title="Clear reward"
                        aria-label={`Clear day ${day} reward`}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="reward-preview-empty">
                      <div className="reward-preview-empty-inner">
                        <span className="reward-preview-empty-icon" aria-hidden>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                          </svg>
                        </span>
                        <span className="reward-preview-empty-text">
                          <strong>No reward yet</strong>
                          Choose Token or Voucher above to configure this day.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="reward-grid-footer">
        <div className="reward-grid-footer-row">
          {!allConfigured && (
            <span className="reward-grid-hint">
              All 7 days must be configured before saving
            </span>
          )}
          <div className="reward-grid-actions">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!hasUnsavedChanges}
              onClick={onSaveDraft}
            >
              Save draft
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!allConfigured || saving || !hasUnsavedChanges}
              onClick={onSave}
            >
              {saving ? 'Saving…' : 'Save rewards'}
            </button>
          </div>
        </div>
      </div>

      <TokenModal
        isOpen={modalType === 'token' && modalDay !== null}
        onClose={closeModal}
        onApply={(data) => handleApply(modalDay, data)}
        initialToken={modalDay !== null ? getReward(modalDay) : null}
      />

      <VoucherModal
        isOpen={modalType === 'voucher' && modalDay !== null}
        onClose={closeModal}
        onApply={(data) => handleApply(modalDay, data)}
        initialVoucher={modalDay !== null ? getReward(modalDay) : null}
      />
    </div>
  );
}

export default RewardGrid;
